// Our custom utility functions
import React from "react";
import { fi, enGB, sv } from "date-fns/locale";
import { format, parseISO } from "date-fns";
import BigNumber from "bignumber.js";
import { Icons } from "@evenli-platform/ui/svg/icons";
import { CalendarClock } from "lucide-react";
import { z } from "zod";

export const capitalizeFirstLetter = (string: string) => {
  if (string) return string.charAt(0).toUpperCase() + string.slice(1);
  return "";
};

export const capitalizeFirstLetterOfEachEnglishWord = (
  string: string,
  locale: string,
) => {
  if (locale === "en-US" || locale === "en-GB" || locale === "en") {
    return string
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } else {
    return string;
  }
};

export const isValidDatabaseId = (id: number): boolean => {
  return Number.isInteger(id) && id > 0 && id <= Number.MAX_SAFE_INTEGER;
};

export const sortSockets = <T extends { externalId: number }>(
  sockets: T[],
): T[] => {
  return sockets.sort((a, b) => (a.externalId || 0) - (b.externalId || 0));
};

export const handlePriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
  let value = e.target.value;

  if (value === "") {
    value = "0.00";
  } else {
    // Remove leading zeros, but keep a single "0" if the value is "0.00"
    value = value.replace(/^0+(?=\d)/, "");
    // If the input starts with a dot or comma, prepend a zero
    if (value.match(/^[.,]/)) {
      value = "0" + value;
    }
    // Add one or two zeros if there are no digits after the dot
    if (value.indexOf(".") === -1) {
      value += ".00";
    } else {
      const parts = value.split(".");
      if (parts[1].length === 0) {
        value += "00";
      } else if (parts[1].length === 1) {
        value += "0";
      }
    }
  }
  return value;
};

export const handlePriceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
  let value = e.target.value;

  // Remove non-digit characters, except for dots and commas
  value = value.replace(/[^\d.,]/g, "");

  // Replace multiple dots or commas with a single dot
  value = value.replace(/([.,])+/g, "$1");

  // Ensure only one dot or comma and limit to two digits after the dot
  const parts = value.split(/[.,]/);
  if (parts.length > 2) {
    value = parts[0] + "." + (parts[1] + parts[2]).slice(0, 3);
  } else if (parts.length === 2) {
    value = parts[0] + "." + parts[1].slice(0, 3);
  }

  return value;
};

export function getCalendarLocale(locale: string) {
  switch (locale) {
    case "fi":
      return fi;
    case "en":
      return enGB;
    case "sw":
      return sv;
    default:
      return fi;
  }
}

// Replace trimmed empty strings with null in Object's values
export function emptyStringsAndUndefinedToNull<
  T extends Record<string, unknown>,
>(data: T): T {
  const result = { ...data };

  Object.keys(result).forEach((key) => {
    const k = key as keyof T;
    if (
      (typeof result[k] === "string" && result[k].trim() === "") ||
      result[k] === undefined
    ) {
      (result[k] as unknown as string | null) = null;
    }
  });

  return result;
}

export const calculateLineItemCost = (
  taxRatePercentage: number,
  unitPrice: number,
  desiredQuantity: number,
) => {
  const quantity = new BigNumber(desiredQuantity).decimalPlaces(4).toNumber();
  const taxRate = new BigNumber(taxRatePercentage)
    .dividedBy(100)
    .decimalPlaces(3)
    .toNumber();
  const unitTotalAmount = new BigNumber(unitPrice)
    .multipliedBy(1 + taxRate)
    .decimalPlaces(2)
    .toNumber();
  const totalAmount = new BigNumber(unitTotalAmount)
    .multipliedBy(quantity)
    .decimalPlaces(2)
    .toNumber();
  const subtotalAmount = new BigNumber(totalAmount)
    .dividedBy(1 + taxRate)
    .decimalPlaces(2)
    .toNumber();
  const taxAmount = new BigNumber(totalAmount)
    .minus(subtotalAmount)
    .decimalPlaces(2)
    .toNumber();

  return {
    unitPrice,
    quantity,
    subtotalAmount,
    taxAmount,
    totalAmount,
    taxRate,
    taxRatePercentage,
  };
};

export const renderTimeUnit = (
  value: number,
  unit: string,
  addSpace = true,
  t: (key: string) => string,
): React.ReactNode => {
  if (value <= 0) return null;
  return (
    <React.Fragment>
      {value}
      <span className="ml-[1px] text-sm">{t(unit)}</span>
      {addSpace && " "}
    </React.Fragment>
  );
};

export const renderStartedBy = (
  startedBy: string | null | undefined,
  t: (key: string) => string,
): React.ReactNode => {
  if (!startedBy) {
    return (
      <span className="text-xs opacity-50 whitespace-nowrap">
        {t("not_defined")}
      </span>
    );
  }

  let icon = null;
  let displayText = startedBy;

  if (startedBy.toLowerCase() === "tag") {
    icon = <Icons.chargingTag className="h-4 w-4" />;
    displayText = t("started_by_tag");
  } else if (startedBy.toLowerCase() === "mobile-app") {
    icon = <Icons.phone className="h-4 w-4" />;
    displayText = t("started_by_mobile_app");
  } else if (startedBy.toLowerCase() === "heating-schedule") {
    icon = <CalendarClock className="h-4 w-4" />;
    displayText = t("started_by_heating_schedule");
  } else if (startedBy.toLowerCase() === "ocpi") {
    icon = <Icons.ocpi className="h-4 w-4" />;
    displayText = "OCPI";
  } else if (startedBy.toLowerCase() === "unknown") {
    icon = <Icons.unknownStarter className="h-4 w-4" />;
    displayText = t("unknown");
  }

  return (
    <div className="whitespace-nowrap text-xs flex items-center gap-1.5">
      {icon}
      <span>{displayText}</span>
    </div>
  );
};

export const calculateDuration = (
  startedAt: Date | string,
  finishedAt: Date | string,
  t: (key: string) => string,
  isFinished: boolean,
): React.ReactNode => {
  const startDate = new Date(startedAt);
  const endDate = new Date(finishedAt);

  const totalSeconds = Math.floor(
    (endDate.getTime() - startDate.getTime()) / 1000,
  );
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (totalSeconds === 0) {
    return (
      <React.Fragment>
        0<span className="ml-[1px] text-sm">{t("s")}</span>
      </React.Fragment>
    );
  }

  if (totalSeconds < 60 && !isFinished) {
    return (
      <React.Fragment>
        {"< 1"}
        <span className="ml-[1px] text-sm">{t("m")}</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {renderTimeUnit(hours, "h", true, t)}
      {renderTimeUnit(minutes, "m", true, t)}
      {isFinished ? renderTimeUnit(seconds, "s", false, t) : null}
    </React.Fragment>
  );
};

export const trimOrNullify = (
  value: string | null | undefined,
): string | null => {
  return value?.trim() || null;
};

export const isDateExpired = (date: string | Date | null | undefined) => {
  if (!date) return "active";
  return new Date(date) < new Date() ? "expired" : "active";
};

export const formatSafeDate = (
  dateValue: string | Date | null | undefined,
): string => {
  if (!dateValue) return "...";

  try {
    const date =
      typeof dateValue === "string" ? parseISO(dateValue) : dateValue;
    return format(date, "dd.MM.yyyy");
  } catch (_error) {
    console.warn("Invalid date format:", dateValue);
    return "...";
  }
};

export const stringTrimmedNotEmptyNullable = (maxLength?: number) => {
  let schema = z
    .string()
    .trim()
    .transform((val) => (val === "" ? null : val))
    .nullable();

  if (maxLength) {
    schema = z
      .string()
      .trim()
      .max(maxLength)
      .transform((val) => (val === "" ? null : val))
      .nullable();
  }

  return schema;
};

/**
 * Removes properties with undefined values from an object.
 * Returns a new object with the same structure but without undefined properties.
 * Works only on highest level of the object and don't go deeper in the nested objects.
 *
 * @param obj - The object to filter
 * @returns A new object with undefined properties removed
 */
export function removeUndefinedFields<O extends Record<string, unknown>>(
  obj: O,
): { [K in keyof O as undefined extends O[K] ? never : K]: O[K] } {
  Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key]);
  return obj;
}

export interface QuarterPeriod {
  startDate: string;
  endDate: string;
  label?: string;
}

export const getQuarterDates = (
  quarter: string,
  year: string,
): QuarterPeriod => {
  const yearNum = parseInt(year);
  const quarterNum = parseInt(quarter);

  let startDate: Date;
  let endDate: Date;

  switch (quarterNum) {
    case 1:
      startDate = new Date(yearNum, 0, 1);
      endDate = new Date(yearNum, 2, 31);
      break;
    case 2:
      startDate = new Date(yearNum, 3, 1);
      endDate = new Date(yearNum, 5, 30);
      break;
    case 3:
      startDate = new Date(yearNum, 6, 1);
      endDate = new Date(yearNum, 8, 30);
      break;
    case 4:
      startDate = new Date(yearNum, 9, 1);
      endDate = new Date(yearNum, 11, 31);
      break;
    default:
      throw new Error("Invalid quarter");
  }

  return {
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
  };
};
